import { useEffect, useId } from 'react';
import { Globe, Lock, List, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/Input';

export type ListaFormModalData = {
    nome: string;
    descricao: string;
    publica: boolean;
};

export type ListaFormModalProps = {
    open: boolean;
    mode: 'create' | 'edit';
    formData: ListaFormModalData;
    onFormDataChange: (next: ListaFormModalData) => void;
    isSubmitting: boolean;
    onClose: () => void;
    onSubmit: (e: React.FormEvent) => void;
};

export default function ListaFormModal({
    open,
    mode,
    formData,
    onFormDataChange,
    isSubmitting,
    onClose,
    onSubmit,
}: ListaFormModalProps) {
    const titleId = useId();

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    useEffect(() => {
        if (!open || isSubmitting) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, isSubmitting, onClose]);

    if (!open) return null;

    const title = mode === 'edit' ? 'Editar lista' : 'Nova lista';
    const subtitle =
        mode === 'edit'
            ? 'Atualize o nome, a descrição ou a visibilidade.'
            : 'Dê um nome e escolha se a lista é visível para outros.';

    return (
        <div
            className="fixed inset-0 z-[999] flex items-end justify-center p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
        >
            <button
                type="button"
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                aria-label="Fechar"
                disabled={isSubmitting}
                onClick={() => !isSubmitting && onClose()}
            />
            <div className="relative flex max-h-[min(92dvh,640px)] w-full max-w-md flex-col overflow-hidden rounded-t-[1.35rem] border border-white/10 border-b-0 bg-neutral-950 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200 sm:rounded-2xl sm:border-b">
                <div className="h-1 w-full shrink-0 bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500/80" />
                <div className="flex items-start justify-between gap-3 border-b border-white/5 px-5 pb-4 pt-4">
                    <div className="flex min-w-0 gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                            <List className="size-5 text-orange-500/90" aria-hidden />
                        </div>
                        <div className="min-w-0 pt-0.5">
                            <h3 id={titleId} className="text-lg font-bold tracking-tight text-white">
                                {title}
                            </h3>
                            <p className="mt-1 text-xs leading-relaxed text-white/45">{subtitle}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => onClose()}
                        className="shrink-0 rounded-lg p-2 text-white/40 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-40"
                        aria-label="Fechar"
                    >
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                        <div>
                            <label htmlFor="lista-form-nome" className="mb-1.5 block text-xs font-medium text-white/50">
                                Nome
                            </label>
                            <Input
                                id="lista-form-nome"
                                type="text"
                                required
                                value={formData.nome}
                                onChange={(e) => onFormDataChange({ ...formData, nome: e.target.value })}
                                placeholder="Nome da lista"
                                className="rounded-xl py-3 placeholder:text-white/30"
                            />
                        </div>
                        <div>
                            <label htmlFor="lista-form-desc" className="mb-1.5 block text-xs font-medium text-white/50">
                                Descrição
                            </label>
                            <textarea
                                id="lista-form-desc"
                                value={formData.descricao}
                                onChange={(e) => onFormDataChange({ ...formData, descricao: e.target.value })}
                                placeholder="Opcional — aparece nas listas públicas"
                                rows={3}
                                className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-white/30 focus:border-orange-500/50"
                            />
                        </div>
                        <div>
                            <p className="mb-2 text-xs font-medium text-white/50">Visibilidade</p>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => onFormDataChange({ ...formData, publica: false })}
                                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-3 py-3.5 text-sm font-medium transition-colors ${
                                        !formData.publica
                                            ? 'border-orange-500/40 bg-orange-500/10 text-orange-300'
                                            : 'border-white/10 text-white/45 hover:bg-white/5'
                                    }`}
                                >
                                    <Lock className="size-4" aria-hidden />
                                    Privada
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onFormDataChange({ ...formData, publica: true })}
                                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-3 py-3.5 text-sm font-medium transition-colors ${
                                        formData.publica
                                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                            : 'border-white/10 text-white/45 hover:bg-white/5'
                                    }`}
                                >
                                    <Globe className="size-4" aria-hidden />
                                    Pública
                                </button>
                            </div>
                            <p className="mt-2 text-[11px] leading-snug text-white/35">
                                Listas privadas só você vê. Públicas podem aparecer na comunidade e receber seguidores.
                            </p>
                        </div>
                    </div>
                    <div className="flex shrink-0 gap-2 border-t border-white/5 bg-neutral-950/95 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur-md sm:pb-4">
                        <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => onClose()}
                            className="flex-1 rounded-xl border border-white/10 py-3 text-sm font-semibold text-white/70 transition-colors hover:border-white/20 hover:bg-white/[0.04] hover:text-white disabled:opacity-40"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-orange-600 py-3 text-sm font-bold text-white transition-colors hover:bg-orange-500 disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="size-4 animate-spin" aria-hidden />
                                    Salvando…
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
